import { useEffect, useRef } from "react";

/**
 * Fires a single fire-and-forget page-view beacon to the admin CMS.
 * Mounted from individual page modules with their contentType + id.
 *
 * Uses `navigator.sendBeacon` first (no CORS preflight, no errors on
 * unload); falls back to `fetch(keepalive:true)` for older browsers.
 * The ref-based lock makes the beacon fire once even when React 18
 * StrictMode double-invokes effects in dev.
 */
const PageViewBeacon = ({ contentType, contentId, slug, path }) => {
  const fired = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (fired.current) return;
    fired.current = true;

    const base =
      (process.env.NEXT_PUBLIC_CMS_API_BASE || "http://localhost:8000") +
      "/api/v1/track-view";
    const payload = JSON.stringify({
      content_type: contentType,
      content_id: contentId ?? null,
      slug: slug ?? null,
      path: path || window.location.pathname,
      referrer: document.referrer || null,
    });

    const blob = new Blob([payload], { type: "text/plain" });
    if (typeof navigator.sendBeacon === "function") {
      try {
        navigator.sendBeacon(base, blob);
        return;
      } catch (_) {
        // fall through to fetch keepalive
      }
    }
    try {
      fetch(base, {
        method: "POST",
        body: payload,
        headers: { "Content-Type": "text/plain" },
        keepalive: true,
      }).catch(() => {});
    } catch (_) {
      // Tracking is best-effort; never surface failures to the user.
    }
  }, [contentType, contentId, slug, path]);

  return null;
};

export default PageViewBeacon;

